import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { fi } from 'date-fns/locale';
import { Helmet } from 'react-helmet';
import { Button, Container, Form } from 'react-bootstrap';

import { Icon } from '@lomarengas/design-system';
import { format } from 'date-fns';
import qs from 'qs';
import {
  addStatusToast,
  DataTable,
  DataTableItem,
  Loader,
  PaginationComponent,
  SearchBar,
  SearchFilterDropdown,
  SectionHeader,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  AccountsFilter,
  AccountUpdateData,
  getAllCustomerGroupsAsync,
  getAllCustomersFromDbAsync,
  getAllRolesAsync,
  markAccountDeletedAsyncAsync,
  selectAllAccountsLoadingStatus,
  selectAllCustomersFromDb,
  selectAllCustomersPagination,
  selectCustomerGroups,
  selectCustomerRole,
} from '../Users/identityUsersSlice';
import { LOADING_STATUS, LOCALES } from '../../common/constants';
import { InitialPaginationParameters } from '../../common/types/pagination.types';
import {
  selectWebShopCustomerState,
  setWebShopCustomerState,
  WebShopCustomerItem,
} from '../bookings/bookingsSlice';
import {
  getCountriesAsync,
  selectCountryItems,
} from '../countries/countriesSlice';
import { getTranslation } from '../../common/utils/translationUtils';
import { dateToDateOnlyString } from '../../common/utils/dateUtils';

function Customers() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const history = useNavigate();
  const state = location.state as {
    payer: string;
    bookingId: string;
    bookingType: string;
    bookingLocale: string;
    webshopCustomer: {
      lastName: '';
      additionalAddress: '';
      companyName: '';
      customerNumber: '';
      email: '';
      countryId: '';
      firstName: '';
      streetAddress: '';
      mobileNumber: '';
      phoneNumber: '';
      postalCode: '';
      postCity: '';
    };
  }; // Type assertion for TypeScript
  const [hasSearchBeenMade, setHasSearchBeenMade] = useState(false);
  const [searchTerm, setSearchTerm] = useState(null);

  const countries = useAppSelector(selectCountryItems);
  const customerGroups = useAppSelector(selectCustomerGroups);
  const customers = useAppSelector(selectAllCustomersFromDb);
  const accountsLoadingStatus = useAppSelector(selectAllAccountsLoadingStatus);
  const customersPagination = useAppSelector(selectAllCustomersPagination);
  const customerRole = useAppSelector(selectCustomerRole);
  const [isReset, setIsReset] = useState(false);
  const webShopCustomerState = useAppSelector(selectWebShopCustomerState);

  const [accountsSearchFilter, setAccountsSearchFilter] =
    useState<AccountsFilter>({
      PageNumber: InitialPaginationParameters.PageNumber,
      PageSize: InitialPaginationParameters.PageSize,
      searchString: searchTerm,
      culture: LOCALES.Fi,
      isActive: true,
      fullEmailMarketingAllowed: null,
      marketingAuthorizationViaSms: null,
    });
  const [initState, setInitState] = useState({
    payer: '',
    bookingId: '',
    bookingType: '',
    bookingLocale: '',
    webshopCustomer: {
      lastName: '',
      additionalAddress: '',
      companyName: '',
      customerNumber: '',
      email: '',
      countryId: '',
      firstName: '',
      streetAddress: '',
      mobileNumber: '',
      phoneNumber: '',
      postalCode: '',
      postCity: '',
    } as WebShopCustomerItem,
  });
  const [accountsTableItems, setAccountsTableItems] = useState<DataTableItem[]>(
    [],
  );

  const handleInputChange = event => {
    const { target } = event;
    const { value, name } = target;
    setSearchTerm(value);
    // console.debug('handleInputChange:', value, name);
    // setAccountsSearchFilter(prevState => ({
    //   ...prevState,
    //   [name]: value,
    // }));
  };
  const doCustomSearch = (searchTermIn, searchFilterRole) => {
    const modifiedSearchFilter = {
      ...accountsSearchFilter,
      PageNumber: InitialPaginationParameters.PageNumber,
      searchString: searchTermIn,
      roleIds: searchFilterRole,
    };
    setSearchParams(searchParams);
    setSearchTerm(modifiedSearchFilter.searchString);
    dispatch(getAllCustomersFromDbAsync(modifiedSearchFilter));
    setHasSearchBeenMade(true);
    setIsReset(false);
  };
  const doSearch = event => {
    if (event !== null) {
      event.preventDefault();
    }
    if (customerRole === null || customerRole === undefined) {
      return;
    }

    const searchFilterRole = customerRole.map(role => role.id);

    const modifiedSearchFilter = {
      ...accountsSearchFilter,
      PageNumber: InitialPaginationParameters.PageNumber,
      searchString: searchTerm,
      roleIds: searchFilterRole,
    };

    setAccountsSearchFilter(prevState => ({
      ...prevState,
      searchString: searchTerm,
    }));

    // Update URL query parameters
    searchParams.set('isActive', String(accountsSearchFilter.isActive));
    if (accountsSearchFilter.fullEmailMarketingAllowed !== null) {
      searchParams.set(
        'fullEmailMarketingAllowed',
        String(accountsSearchFilter.fullEmailMarketingAllowed),
      );
    } else {
      searchParams.delete('fullEmailMarketingAllowed');
    }
    if (accountsSearchFilter.marketingAuthorizationViaSms !== null) {
      searchParams.set(
        'marketingAuthorizationViaSms',
        String(accountsSearchFilter.marketingAuthorizationViaSms),
      );
    } else {
      searchParams.delete('marketingAuthorizationViaSms');
    }
    if (accountsSearchFilter.createdFrom) {
      searchParams.set(
        'createdFrom',
        format(accountsSearchFilter.createdFrom, 'yyyy-MM-dd'),
      );
    }
    if (accountsSearchFilter.createdTo) {
      searchParams.set(
        'createdTo',
        format(accountsSearchFilter.createdTo, 'yyyy-MM-dd'),
      );
    }
    if (accountsSearchFilter.updatedFrom) {
      searchParams.set(
        'updatedFrom',
        format(accountsSearchFilter.updatedFrom, 'yyyy-MM-dd'),
      );
    }
    if (accountsSearchFilter.updatedTo) {
      searchParams.set(
        'updatedTo',
        format(accountsSearchFilter.updatedTo, 'yyyy-MM-dd'),
      );
    }
    if (accountsSearchFilter.bookingCreatedFrom) {
      searchParams.set(
        'bookingCreatedFrom',
        format(accountsSearchFilter.bookingCreatedFrom, 'yyyy-MM-dd'),
      );
    }
    if (accountsSearchFilter.bookingCreatedTo) {
      searchParams.set(
        'bookingCreatedTo',
        format(accountsSearchFilter.bookingCreatedTo, 'yyyy-MM-dd'),
      );
    }
    if (accountsSearchFilter.bookingArrivalFrom) {
      searchParams.set(
        'bookingArrivalFrom',
        format(accountsSearchFilter.bookingArrivalFrom, 'yyyy-MM-dd'),
      );
    }
    if (accountsSearchFilter.bookingArrivalTo) {
      searchParams.set(
        'bookingArrivalTo',
        format(accountsSearchFilter.bookingArrivalTo, 'yyyy-MM-dd'),
      );
    }
    if (accountsSearchFilter.bookingDepartureFrom) {
      searchParams.set(
        'bookingDepartureFrom',
        format(accountsSearchFilter.bookingDepartureFrom, 'yyyy-MM-dd'),
      );
    }
    if (accountsSearchFilter.bookingDepartureTo) {
      searchParams.set(
        'bookingDepartureTo',
        format(accountsSearchFilter.bookingDepartureTo, 'yyyy-MM-dd'),
      );
    }
    if (accountsSearchFilter.companyId) {
      searchParams.set('companyId', accountsSearchFilter.companyId);
    }
    if (accountsSearchFilter.invoiceByInternet !== null) {
      searchParams.set(
        'invoiceByInternet',
        String(accountsSearchFilter.invoiceByInternet),
      );
    } else {
      searchParams.delete('invoiceByInternet');
    }
    if (accountsSearchFilter.countryId) {
      searchParams.set('countryId', accountsSearchFilter.countryId);
    } else {
      searchParams.delete('countryId');
    }
    if (accountsSearchFilter.customerGroupId) {
      searchParams.set('customerGroupId', accountsSearchFilter.customerGroupId);
    } else {
      searchParams.delete('customerGroupId');
    }
    if (modifiedSearchFilter.searchString) {
      searchParams.set('searchString', modifiedSearchFilter.searchString);
    } else {
      searchParams.delete('searchString');
    }
    if (modifiedSearchFilter.roleIds) {
      searchParams.delete('roleIds');
      modifiedSearchFilter.roleIds.forEach(roleId => {
        searchParams.append('roleIds', roleId);
      });
    }

    setSearchParams(searchParams);
    setSearchTerm(modifiedSearchFilter.searchString);
    dispatch(getAllCustomersFromDbAsync(modifiedSearchFilter));
    setHasSearchBeenMade(true);
    setIsReset(false);
  };

  const initFetch = useCallback(() => {
    dispatch(
      getAllRolesAsync({
        pageNumber: 1,
        pageSize: 10,
        culture: LOCALES.Fi,
        searchString: '',
      }),
    );
  }, [dispatch]);
  useEffect(() => {
    if (state !== null && state !== undefined && state.payer === 'pick') {
      setInitState(state);
      // Sync sessionStorage with navigation state to ensure correct booking context
      if (state.bookingId) {
        sessionStorage.setItem('bookingId', state.bookingId ?? '');
      }
      if (state.bookingType) {
        sessionStorage.setItem('bookingType', String(state.bookingType ?? ''));
      }
      if (state.bookingLocale) {
        sessionStorage.setItem('bookingLocale', state.bookingLocale ?? '');
      }
      if (
        state.webshopCustomer !== null &&
        state.webshopCustomer !== undefined &&
        state.webshopCustomer.lastName
      ) {
        console.log('setWebshopCustomer:', state.webshopCustomer);
        setAccountsTableItems([]);
        dispatch(setWebShopCustomerState(state.webshopCustomer)).then(() => {
          console.log(
            'setted webshop customer',
            state.webshopCustomer.lastName,
          );
          doCustomSearch(
            `${state.webshopCustomer.firstName} ${
              state.webshopCustomer.lastName
            }`,
            ['259DCF7A-9512-4163-9D77-C44D97FDDCD1'],
          );
        });
      }
    }
    dispatch(
      getCountriesAsync({
        PageNumber: 1,
        PageSize: 1000000,
        searchString: '',
        culture: LOCALES.Fi,
      }),
    );
  }, [dispatch, state]);

  useEffect(() => {
    dispatch(
      getAllCustomerGroupsAsync({
        PageNumber: 1,
        PageSize: 1000000,
        SearchString: '',
        Culture: LOCALES.Fi,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    const isActive = searchParams.get('isActive');
    const createdFrom = searchParams.get('createdFrom');
    const createdTo = searchParams.get('createdTo');
    const updatedFrom = searchParams.get('updatedFrom');
    const updatedTo = searchParams.get('updatedTo');
    const bookingCreatedFrom = searchParams.get('bookingCreatedFrom');
    const bookingCreatedTo = searchParams.get('bookingCreatedTo');
    const bookingArrivalFrom = searchParams.get('bookingArrivalFrom');
    const bookingArrivalTo = searchParams.get('bookingArrivalTo');
    const bookingDepartureFrom = searchParams.get('bookingDepartureFrom');
    const bookingDepartureTo = searchParams.get('bookingDepartureTo');
    const companyId = searchParams.get('companyId');
    const invoiceByInternet = searchParams.get('invoiceByInternet');
    const countryId = searchParams.get('countryId');
    const customerGroupId = searchParams.get('customerGroupId');
    const searchString = searchParams.get('searchString');
    const roleIds = searchParams.getAll('roleIds');

    const parseBool = (val: string | null) => {
      if (val === 'true') return true;
      if (val === 'false') return false;
      return null;
    };

    setAccountsSearchFilter(prevState => ({
      ...prevState,
      isActive: isActive ? isActive === 'true' : true,
      fullEmailMarketingAllowed: parseBool(
        searchParams.get('fullEmailMarketingAllowed'),
      ),
      marketingAuthorizationViaSms: parseBool(
        searchParams.get('marketingAuthorizationViaSms'),
      ),
      invoiceByInternet: parseBool(invoiceByInternet),
      createdFrom: createdFrom ? new Date(createdFrom) : null,
      createdTo: createdTo ? new Date(createdTo) : null,
      updatedFrom: updatedFrom ? new Date(updatedFrom) : null,
      updatedTo: updatedTo ? new Date(updatedTo) : null,
      bookingCreatedFrom: bookingCreatedFrom
        ? new Date(bookingCreatedFrom)
        : null,
      bookingCreatedTo: bookingCreatedTo ? new Date(bookingCreatedTo) : null,
      bookingArrivalFrom: bookingArrivalFrom
        ? new Date(bookingArrivalFrom)
        : null,
      bookingArrivalTo: bookingArrivalTo ? new Date(bookingArrivalTo) : null,
      bookingDepartureFrom: bookingDepartureFrom
        ? new Date(bookingDepartureFrom)
        : null,
      bookingDepartureTo: bookingDepartureTo
        ? new Date(bookingDepartureTo)
        : null,
      companyId: companyId || null,
      countryId: countryId || null,
      customerGroupId: customerGroupId || null,
      roleIds,
      searchString: searchString || '',
    }));

    setSearchTerm(searchString || '');

    if (
      searchString ||
      roleIds.length > 0 ||
      companyId ||
      countryId ||
      customerGroupId ||
      isActive !== null ||
      searchParams.get('fullEmailMarketingAllowed') !== null ||
      searchParams.get('marketingAuthorizationViaSms') !== null ||
      invoiceByInternet !== null
    ) {
      dispatch(
        getAllCustomersFromDbAsync({
          ...accountsSearchFilter,
          PageNumber: InitialPaginationParameters.PageNumber,
          searchString,
          roleIds,
          isActive: isActive ? isActive === 'true' : true,
          fullEmailMarketingAllowed: parseBool(
            searchParams.get('fullEmailMarketingAllowed'),
          ),
          marketingAuthorizationViaSms: parseBool(
            searchParams.get('marketingAuthorizationViaSms'),
          ),
          invoiceByInternet: parseBool(invoiceByInternet),
          companyId: companyId || null,
          countryId: countryId || null,
          customerGroupId: customerGroupId || null,
        }),
      );
      setHasSearchBeenMade(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (customers && customers.length > 0 && !isReset) {
      const formattedAccountsList: DataTableItem[] = [];
      if (initState != null && initState.payer === 'pick') {
        // console.debug('pick:', initState);
        customers.forEach(item => {
          formattedAccountsList.push({
            ItemId: item.id,
            Items: [
              item.customerNumber,
              `${item.firstName} ${item.lastName}`,
              item.companyName,
              item.contactPersonName,
              item.postStreetAddress,
              item.postCity,
              item.userName,
              item.phoneNumber,
              item.hasUserLogin ? '✔️' : '',
            ],
            Url: `/bookings/${initState.bookingId}/${item.id}?bookingType=${initState.bookingType}`,
          });
        });
      } else {
        // console.debug('pick Not:', initState);
        customers.forEach(item => {
          const queryParams = qs.stringify(
            {
              ...accountsSearchFilter,
              roleIds: accountsSearchFilter.roleIds,
            },
            { arrayFormat: 'repeat' },
          );

          formattedAccountsList.push({
            ItemId: item.id,
            Items: [
              item.customerNumber,
              `${item.firstName} ${item.lastName}`,
              item.companyName,
              item.contactPersonName,
              item.postStreetAddress,
              item.postCity,
              item.userName,
              item.phoneNumber,
              item.hasUserLogin ? '✔️' : '',
            ],
            Url: `/customers/details/${item.id}?${queryParams}`,
          });
        });
      }
      setAccountsTableItems(formattedAccountsList);
      if (webShopCustomerState && !searchParams.get('searchString')) {
        const webshopCustomer = webShopCustomerState as WebShopCustomerItem;
        if (
          webshopCustomer.firstName !== null &&
          webshopCustomer.firstName !== undefined &&
          webshopCustomer.lastName !== undefined &&
          webshopCustomer.lastName !== null
        ) {
          setSearchTerm(
            `${webshopCustomer.firstName} ${webshopCustomer.lastName}`,
          );
        } else if (
          webshopCustomer.lastName !== undefined &&
          webshopCustomer.lastName !== null
        ) {
          setSearchTerm(`${webshopCustomer.lastName}`);
        }
      }
    } else {
      if (webShopCustomerState && !searchParams.get('searchString')) {
        const webshopCustomer = webShopCustomerState as WebShopCustomerItem;
        if (
          webshopCustomer.firstName !== null &&
          webshopCustomer.firstName !== undefined &&
          webshopCustomer.lastName !== undefined &&
          webshopCustomer.lastName !== null
        ) {
          setSearchTerm(
            `${webshopCustomer.firstName} ${webshopCustomer.lastName}`,
          );
        } else if (
          webshopCustomer.lastName !== undefined &&
          webshopCustomer.lastName !== null
        ) {
          setSearchTerm(`${webshopCustomer.lastName}`);
        }
      }
      setAccountsTableItems([]);
    }
  }, [
    customers,
    initState,
    accountsSearchFilter,
    isReset,
    webShopCustomerState,
    searchParams,
  ]);

  useEffect(() => {
    if (accountsTableItems && accountsTableItems.length > 0) {
      setHasSearchBeenMade(true);
    }
  }, [accountsTableItems]);
  useEffect(() => {}, [webShopCustomerState]);

  useEffect(() => {
    initFetch();
  }, [initFetch]);
  const HandlePaginationClick = (Page: number) => {
    setSearchParams(searchParams);
    const searchFilterRole = customerRole.map(role => role.id);
    const paginatedFiltering = accountsSearchFilter;
    paginatedFiltering.PageNumber = Page;
    paginatedFiltering.searchString = searchTerm;
    paginatedFiltering.roleIds = searchFilterRole;
    setAccountsSearchFilter(paginatedFiltering);
    dispatch(getAllCustomersFromDbAsync(paginatedFiltering));
  };

  function addNewAction() {
    // Determine booking context prioritizing sessionStorage but safely falling back to initState
    const ssBookingId = sessionStorage.getItem('bookingId') || '';
    const ssBookingType = sessionStorage.getItem('bookingType') || '';
    const ssBookingLocale = sessionStorage.getItem('bookingLocale') || '';
    const hasStoredId = ssBookingId && ssBookingId.length > 1;

    const effectiveBookingId = hasStoredId ? ssBookingId : initState.bookingId;
    const effectiveBookingType = hasStoredId
      ? ssBookingType || initState.bookingType
      : initState.bookingType;
    const effectiveBookingLocale = hasStoredId
      ? ssBookingLocale || initState.bookingLocale
      : initState.bookingLocale;

    if (webShopCustomerState) {
      const webshopCustomer = webShopCustomerState as WebShopCustomerItem;
      history(`/users/addNewCustomer`, {
        state: {
          webshopCustomer,
          bookingId: effectiveBookingId,
          bookingType: effectiveBookingType,
          bookingLocale: effectiveBookingLocale,
        },
      });
    } else {
      history(`/users/addNewCustomer`, {
        state: {
          bookingId: effectiveBookingId,
          bookingType: effectiveBookingType,
          bookingLocale: effectiveBookingLocale,
        },
      });
    }
  }

  const AddNewButton: JSX.Element = (
    <Button type="submit" variant="blue" onClick={() => addNewAction()}>
      <Icon CssClass="me-8" AriaLabel="Uusi" Name="plus" />
      Uusi asiakas
    </Button>
  );
  const handlePopUpUserAccountLogin = async (AccountId: string) => {
    // console.debug('tarkista alku:', webShopCustomerState);
    if (webShopCustomerState) {
      const currentBookingIdstore = sessionStorage.getItem('bookingId');
      const currentWebshopCustomer =
        webShopCustomerState as WebShopCustomerItem;

      if (
        currentWebshopCustomer !== null &&
        currentWebshopCustomer !== undefined &&
        currentWebshopCustomer.lastName !== null &&
        currentWebshopCustomer.lastName !== ''
      ) {
        // console.debug('tarkista alku initState:', initState);
        if (
          initState.bookingId !== null &&
          initState.bookingId !== undefined &&
          initState.bookingId.length > 1
        ) {
          // console.debug('tarkista alku initState Ok:', initState);
          sessionStorage.setItem('bookingId', initState.bookingId);
          sessionStorage.setItem('bookingLocale', initState.bookingLocale);
          sessionStorage.setItem('bookingType', initState.bookingType);
          const webshopCustomer = webShopCustomerState as WebShopCustomerItem;
          history(`/customers/details/${AccountId}`, {
            state: {
              webshopCustomer,
              bookingType: initState.bookingType,
              bookingLocale: initState.bookingLocale,
              bookingId: initState.bookingId,
            },
          });
        } else {
          // console.debug('tarkista alku initState NOT Ok:', initState);
          setInitState({
            ...initState,
            bookingId: sessionStorage.getItem('bookingId'),
            bookingLocale: sessionStorage.getItem('bookingLocale'),
            bookingType: sessionStorage.getItem('bookingType'),
          });
          const webshopCustomer = webShopCustomerState as WebShopCustomerItem;
          history(`/customers/details/${AccountId}`, {
            state: {
              webshopCustomer,
              bookingType: sessionStorage.getItem('bookingType'),
              bookingId: sessionStorage.getItem('bookingId'),
              bookingLocale: sessionStorage.getItem('bookingLocale'),
            },
          });
        }
      } else if (
        initState.bookingId !== null &&
        initState.bookingId !== undefined
      ) {
        // console.debug('tarkista alku initState ODD path:', initState);
        history(`/customers/details/${AccountId}`, {
          state: {
            bookingId: initState.bookingId,
            bookingType: initState.bookingType,
            bookingLocale: initState.bookingLocale,
          },
        });
      } else {
        // console.debug('tarkista alku initState ODD path 2:', initState);
        const webshopCustomer = currentBookingIdstore;
        history(`/customers/details/${AccountId}`, {
          state: {
            webshopCustomer,
            bookingType: initState.bookingType,
            bookingLocale: initState.bookingLocale,
            bookingId: currentBookingIdstore,
          },
        });
      }
    } else if (sessionStorage.getItem('webshopCustomer') !== null) {
      const webshopCustomer = sessionStorage.getItem('webshopCustomer');
      // console.debug('id3:', initState.bookingId);
      history(`/customers/details/${AccountId}`, {
        state: {
          webshopCustomer,
          bookingType: initState.bookingType,
          bookingLocale: initState.bookingLocale,
          bookingId: initState.bookingId,
        },
      });
    } else {
      setInitState({
        ...initState,
        bookingId: sessionStorage.getItem('bookingId'),
        bookingLocale: sessionStorage.getItem('bookingLocale'),
        bookingType: sessionStorage.getItem('bookingType'),
      });
      // console.debug(
      //   'last resort initState:',
      //   sessionStorage.getItem('bookingId'),
      // );
      if (
        sessionStorage.getItem('bookingId') !== null &&
        sessionStorage.getItem('bookingId') !== undefined &&
        sessionStorage.getItem('bookingId').length > 1
      ) {
        setInitState({
          ...initState,
          bookingId: sessionStorage.getItem('bookingId'),
          bookingLocale: sessionStorage.getItem('bookingLocale'),
          bookingType: sessionStorage.getItem('bookingType'),
        });
        const webshopCustomer = webShopCustomerState as WebShopCustomerItem;
        history(`/customers/details/${AccountId}`, {
          state: {
            webshopCustomer,
            bookingType: sessionStorage.getItem('bookingId'),
            bookingLocale: sessionStorage.getItem('bookingLocale'),
            bookingId: sessionStorage.getItem('bookingId'),
          },
        });
      } else {
        history(`/customers/details/${AccountId}`);
      }
    }
  };
  const handleSearchFilterChange = (field: string, value: unknown) => {
    const formattedValue =
      value instanceof Date ? format(value, 'yyyy-MM-dd') : value;

    setAccountsSearchFilter(prevState => ({
      ...prevState,
      [field]: formattedValue,
    }));
  };
  const handleDeleteUser = (AccountId: string) => {
    const accountUpdateData = {
      id: AccountId,
      contactUpdateData: null,
      accountsRoles: null,
    } as AccountUpdateData;
    dispatch(markAccountDeletedAsyncAsync(accountUpdateData)).then(res => {
      if (res.meta.requestStatus === 'fulfilled') {
        dispatch(
          addStatusToast({
            Message: `Käyttäjä on poistettu.`,
          }),
        );
        dispatch(getAllCustomersFromDbAsync(accountsSearchFilter));
        setHasSearchBeenMade(true);
      } else if (res.meta.requestStatus === 'rejected') {
        dispatch(
          addStatusToast({
            IsFailed: true,
            Message: `Käyttäjän poistamisessa tapahtui virhe.`,
          }),
        );
      }
    });
  };
  const handleResetFilters = () => {
    setAccountsSearchFilter({
      PageNumber: InitialPaginationParameters.PageNumber,
      PageSize: InitialPaginationParameters.PageSize,
      searchString: '',
      culture: LOCALES.Fi,
      roleIds: [],
      isActive: true,
    });
    setSearchTerm('');
    setSearchParams({});
    setAccountsTableItems([]);
    setIsReset(true);
  };

  const handleRemoveFilter = (filterKey: string) => {
    setAccountsSearchFilter(prevState => ({
      ...prevState,
      [filterKey]: null,
    }));
  };

  const formatSelectedValue = (label, value, filterKey: string) => {
    if (
      value === null ||
      value === undefined ||
      value === 0 ||
      value === '0' ||
      value === ''
    ) {
      return null;
    }
    return (
      <span className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8">
        {label}: {value}
        <Button
          variant="link"
          className="p-0"
          onClick={() => handleRemoveFilter(filterKey)}
        >
          <span className="bi bi-x" />
        </Button>
      </span>
    );
  };

  const formatSelectedDates = (
    label: string,
    value: Date,
    value2: Date,
    filterKey: string,
    filterKey2: string,
  ) => {
    if (
      value === null ||
      value2 === null ||
      value === undefined ||
      value2 === undefined ||
      value < new Date('2020-01-01') ||
      value2 < new Date('2020-01-01')
    ) {
      return null;
    }
    return (
      <span className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8">
        {label}: {dateToDateOnlyString(value)} - {dateToDateOnlyString(value2)}
        <Button
          variant="link"
          className="p-0"
          onClick={() => {
            handleRemoveFilter(filterKey);
            handleRemoveFilter(filterKey2);
          }}
        >
          <span className="bi bi-x" />
        </Button>
      </span>
    );
  };

  const formatSelectedCustomerGroup = (
    label: string,
    groupId: string,
    filterKey: string,
  ) => {
    const selectedGroup = customerGroups.find(group => group.id === groupId);
    const groupName = selectedGroup
      ? getTranslation(
          selectedGroup.translations,
          LOCALES.Fi,
          'CustomerGroupName',
        )
      : '';

    if (!groupName) {
      return null;
    }

    return (
      <span className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8">
        {label}: {groupName}
        <Button
          variant="link"
          className="p-0"
          onClick={() => handleRemoveFilter(filterKey)}
        >
          <span className="bi bi-x" />
        </Button>
      </span>
    );
  };

  const formatSelectedCountry = (
    label: string,
    countryId: string,
    filterKey: string,
  ) => {
    const selectedCountry = countries.find(country => country.id === countryId);
    const countryName = selectedCountry ? selectedCountry.name : '';

    if (!countryName) {
      return null;
    }

    return (
      <span className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8">
        {label}: {countryName}
        <Button
          variant="link"
          className="p-0"
          onClick={() => handleRemoveFilter(filterKey)}
        >
          <span className="bi bi-x" />
        </Button>
      </span>
    );
  };

  return (
    <>
      <Helmet>
        <title>Asiakkaat - Haku</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          Breadcrumbs={['Asiakkaat']}
          Children={AddNewButton}
          Title="Asiakkaat"
        />
        <SearchBar
          FreeTextOnChangeHandler={handleInputChange}
          FreeTextPlaceholder={searchTerm ?? 'Hae...'}
          SearchHandler={doSearch}
          IsLoading={accountsLoadingStatus !== LOADING_STATUS.IDLE}
          ResetHandler={handleResetFilters}
          ShowResetButton
          Value={searchTerm}
        >
          {accountsLoadingStatus === LOADING_STATUS.IDLE && (
            <div className="position-relative px-24 pt-0 pb-8">
              <SearchFilterDropdown
                ShowCloseButton={false}
                DefaultValue={false}
                Id="customers-filter-active"
                Label="Aktiivisuus"
                Value={accountsSearchFilter.isActive ?? false}
              >
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="customer-active-switch"
                    label="Aktiivinen"
                    checked={accountsSearchFilter.isActive}
                    onChange={e =>
                      handleSearchFilterChange('isActive', e.target.checked)
                    }
                  />
                </Form.Group>
              </SearchFilterDropdown>
              {/*
              <SearchFilterDropdown
                DefaultValue={null}
                Id="customers-filter-type"
                Label="Asiakastyyppi"
                Value={null}
              >
                <Form.Group className="mb-3">
                  <Form.Select aria-label="Default select example">
                    <option>Open this select menu</option>
                    <option value="1">One</option>
                    <option value="2">Two</option>
                    <option value="3">Three</option>
                  </Form.Select>
                </Form.Group>
              </SearchFilterDropdown>
              */}
              <SearchFilterDropdown
                ShowCloseButton={false}
                DefaultValue={false}
                Id="customer-filter-marketing"
                Label="Markkinointiluvat"
                Value={
                  accountsSearchFilter.fullEmailMarketingAllowed !== null ||
                  accountsSearchFilter.marketingAuthorizationViaSms !== null
                }
              >
                <Form.Group className="mb-2">
                  <Form.Check
                    type="switch"
                    id="customer-marketing-email-switch"
                    label="Markkinointilupa email"
                    checked={
                      accountsSearchFilter.fullEmailMarketingAllowed ?? false
                    }
                    onChange={e =>
                      handleSearchFilterChange(
                        'fullEmailMarketingAllowed',
                        e.target.checked,
                      )
                    }
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="customer-marketing-sms-switch"
                    label="Markkinointilupa SMS"
                    checked={
                      accountsSearchFilter.marketingAuthorizationViaSms ?? false
                    }
                    onChange={e =>
                      handleSearchFilterChange(
                        'marketingAuthorizationViaSms',
                        e.target.checked,
                      )
                    }
                  />
                </Form.Group>
              </SearchFilterDropdown>
              <SearchFilterDropdown
                ShowCloseButton={false}
                DefaultValue=""
                Id="customers-filter-group"
                Label="Asiakasryhmä"
                Value={accountsSearchFilter.customerGroupId || ''}
              >
                <Form.Group className="mb-3">
                  <Form.Select
                    aria-label="Asiakasryhmän valinta"
                    value={accountsSearchFilter.customerGroupId || ''}
                    onChange={e =>
                      handleSearchFilterChange(
                        'customerGroupId',
                        e.target.value || null,
                      )
                    }
                  >
                    <option value="">Valitse ryhmä</option>
                    {customerGroups.map(group => (
                      <option key={group.id} value={group.id}>
                        {getTranslation(
                          group.translations,
                          LOCALES.Fi,
                          'CustomerGroupName',
                        )}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </SearchFilterDropdown>
              <SearchFilterDropdown
                ShowCloseButton={false}
                DefaultValue={false}
                Id="users-filter-created"
                Label="Luotu"
                Value={
                  !!accountsSearchFilter.createdFrom ||
                  !!accountsSearchFilter.createdTo
                }
              >
                <Form.Group className="mb-3">
                  <Form.Label>Alkaen</Form.Label>
                  <DatePicker
                    locale={fi}
                    selected={
                      accountsSearchFilter.createdFrom
                        ? new Date(accountsSearchFilter.createdFrom)
                        : null
                    }
                    dateFormat="dd.MM.yyyy"
                    onChange={date =>
                      handleSearchFilterChange('createdFrom', date)
                    }
                    showWeekNumbers
                    className="form-control"
                    showYearDropdown
                    scrollableYearDropdown
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Päättyen</Form.Label>
                  <DatePicker
                    locale={fi}
                    selected={
                      accountsSearchFilter.createdTo
                        ? new Date(accountsSearchFilter.createdTo)
                        : null
                    }
                    minDate={
                      accountsSearchFilter.createdFrom
                        ? new Date(accountsSearchFilter.createdFrom)
                        : null
                    }
                    dateFormat="dd.MM.yyyy"
                    onChange={date =>
                      handleSearchFilterChange('createdTo', date)
                    }
                    showWeekNumbers
                    className="form-control"
                    disabled={!accountsSearchFilter.createdFrom}
                    showYearDropdown
                    scrollableYearDropdown
                  />
                </Form.Group>
              </SearchFilterDropdown>
              <SearchFilterDropdown
                ShowCloseButton={false}
                DefaultValue={false}
                Id="users-filter-modified"
                Label="Muokattu"
                Value={
                  !!accountsSearchFilter.updatedFrom ||
                  !!accountsSearchFilter.updatedTo
                }
              >
                <Form.Group className="mb-3">
                  <Form.Label>Alkaen</Form.Label>
                  <DatePicker
                    locale={fi}
                    selected={
                      accountsSearchFilter.updatedFrom
                        ? new Date(accountsSearchFilter.updatedFrom)
                        : null
                    }
                    dateFormat="dd.MM.yyyy"
                    onChange={date =>
                      handleSearchFilterChange('updatedFrom', date)
                    }
                    showWeekNumbers
                    className="form-control"
                    showYearDropdown
                    scrollableYearDropdown
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Päättyen</Form.Label>
                  <DatePicker
                    locale={fi}
                    selected={
                      accountsSearchFilter.updatedTo
                        ? new Date(accountsSearchFilter.updatedTo)
                        : null
                    }
                    minDate={
                      accountsSearchFilter.updatedFrom
                        ? new Date(accountsSearchFilter.updatedFrom)
                        : null
                    }
                    dateFormat="dd.MM.yyyy"
                    onChange={date =>
                      handleSearchFilterChange('updatedTo', date)
                    }
                    showWeekNumbers
                    className="form-control"
                    disabled={!accountsSearchFilter.updatedFrom}
                    showYearDropdown
                    scrollableYearDropdown
                  />
                </Form.Group>
              </SearchFilterDropdown>

              <SearchFilterDropdown
                ShowCloseButton={false}
                DefaultValue={false}
                Id="users-filter-bookingcreated"
                Label="Varaus luotu"
                Value={
                  !!accountsSearchFilter.bookingCreatedFrom ||
                  !!accountsSearchFilter.bookingCreatedTo
                }
              >
                <Form.Group className="mb-3">
                  <Form.Label>Alkaen</Form.Label>
                  <DatePicker
                    locale={fi}
                    selected={
                      accountsSearchFilter.bookingCreatedFrom
                        ? new Date(accountsSearchFilter.bookingCreatedFrom)
                        : null
                    }
                    dateFormat="dd.MM.yyyy"
                    onChange={date =>
                      handleSearchFilterChange('bookingCreatedFrom', date)
                    }
                    showWeekNumbers
                    className="form-control"
                    showYearDropdown
                    scrollableYearDropdown
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Päättyen</Form.Label>
                  <DatePicker
                    locale={fi}
                    selected={
                      accountsSearchFilter.bookingCreatedTo
                        ? new Date(accountsSearchFilter.bookingCreatedTo)
                        : null
                    }
                    minDate={
                      accountsSearchFilter.bookingCreatedFrom
                        ? new Date(accountsSearchFilter.bookingCreatedFrom)
                        : null
                    }
                    dateFormat="dd.MM.yyyy"
                    onChange={date =>
                      handleSearchFilterChange('bookingCreatedTo', date)
                    }
                    showWeekNumbers
                    className="form-control"
                    disabled={!accountsSearchFilter.bookingCreatedFrom}
                    showYearDropdown
                    scrollableYearDropdown
                  />
                </Form.Group>
              </SearchFilterDropdown>

              <SearchFilterDropdown
                ShowCloseButton={false}
                DefaultValue={false}
                Id="users-filter-bookingarrival"
                Label="Varaus alkaa"
                Value={
                  !!accountsSearchFilter.bookingArrivalFrom ||
                  !!accountsSearchFilter.bookingArrivalTo
                }
              >
                <Form.Group className="mb-3">
                  <Form.Label>Alkaen</Form.Label>
                  <DatePicker
                    locale={fi}
                    selected={
                      accountsSearchFilter.bookingArrivalFrom
                        ? new Date(accountsSearchFilter.bookingArrivalFrom)
                        : null
                    }
                    dateFormat="dd.MM.yyyy"
                    onChange={date =>
                      handleSearchFilterChange('bookingArrivalFrom', date)
                    }
                    showWeekNumbers
                    className="form-control"
                    showYearDropdown
                    scrollableYearDropdown
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Päättyen</Form.Label>
                  <DatePicker
                    locale={fi}
                    selected={
                      accountsSearchFilter.bookingArrivalTo
                        ? new Date(accountsSearchFilter.bookingArrivalTo)
                        : null
                    }
                    minDate={
                      accountsSearchFilter.bookingArrivalFrom
                        ? new Date(accountsSearchFilter.bookingArrivalFrom)
                        : null
                    }
                    dateFormat="dd.MM.yyyy"
                    onChange={date =>
                      handleSearchFilterChange('bookingArrivalTo', date)
                    }
                    showWeekNumbers
                    className="form-control"
                    disabled={!accountsSearchFilter.bookingArrivalFrom}
                    showYearDropdown
                    scrollableYearDropdown
                  />
                </Form.Group>
              </SearchFilterDropdown>

              <SearchFilterDropdown
                ShowCloseButton={false}
                DefaultValue={false}
                Id="users-filter-bookingdeparture"
                Label="Varaus päättyy"
                Value={
                  !!accountsSearchFilter.bookingDepartureFrom ||
                  !!accountsSearchFilter.bookingDepartureTo
                }
              >
                <Form.Group className="mb-3">
                  <Form.Label>Alkaen</Form.Label>
                  <DatePicker
                    locale={fi}
                    selected={
                      accountsSearchFilter.bookingDepartureFrom
                        ? new Date(accountsSearchFilter.bookingDepartureFrom)
                        : null
                    }
                    dateFormat="dd.MM.yyyy"
                    onChange={date =>
                      handleSearchFilterChange('bookingDepartureFrom', date)
                    }
                    showWeekNumbers
                    className="form-control"
                    showYearDropdown
                    scrollableYearDropdown
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Päättyen</Form.Label>
                  <DatePicker
                    locale={fi}
                    selected={
                      accountsSearchFilter.bookingDepartureTo
                        ? new Date(accountsSearchFilter.bookingDepartureTo)
                        : null
                    }
                    minDate={
                      accountsSearchFilter.bookingDepartureFrom
                        ? new Date(accountsSearchFilter.bookingDepartureFrom)
                        : null
                    }
                    dateFormat="dd.MM.yyyy"
                    onChange={date =>
                      handleSearchFilterChange('bookingDepartureTo', date)
                    }
                    showWeekNumbers
                    className="form-control"
                    disabled={!accountsSearchFilter.bookingDepartureFrom}
                    showYearDropdown
                    scrollableYearDropdown
                  />
                </Form.Group>
              </SearchFilterDropdown>

              <SearchFilterDropdown
                ShowCloseButton={false}
                DefaultValue=""
                Id="users-filter-ycode"
                Label="Y-tunnus"
                Value={accountsSearchFilter.companyId || ''}
              >
                <Form.Group className="mb-3">
                  <Form.Label>Y-tunnus</Form.Label>
                  <Form.Control
                    type="text"
                    value={accountsSearchFilter.companyId || ''}
                    onChange={e =>
                      handleSearchFilterChange('companyId', e.target.value)
                    }
                  />
                </Form.Group>
              </SearchFilterDropdown>
              <SearchFilterDropdown
                ShowCloseButton={false}
                DefaultValue=""
                Id="users-filter-country"
                Label="Maa"
                Value={accountsSearchFilter.countryId || ''}
              >
                <Form.Group>
                  <Form.Select
                    aria-label="Maan valinta"
                    value={accountsSearchFilter.countryId || ''}
                    onChange={e =>
                      handleSearchFilterChange(
                        'countryId',
                        e.target.value || null,
                      )
                    }
                  >
                    <option value="">Valitse maa</option>
                    {countries.map(country => (
                      <option key={country.id} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </SearchFilterDropdown>
              <SearchFilterDropdown
                ShowCloseButton={false}
                DefaultValue={false}
                Id="customers-filter-payment"
                Label="Verkkolasku"
                Value={accountsSearchFilter.invoiceByInternet ?? false}
              >
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="customer-payment-switch"
                    label="Verkkolasku"
                    checked={
                      accountsSearchFilter.invoiceByInternet !== null
                        ? accountsSearchFilter.invoiceByInternet
                        : false
                    }
                    onChange={e =>
                      handleSearchFilterChange(
                        'invoiceByInternet',
                        e.target.checked,
                      )
                    }
                  />
                </Form.Group>
              </SearchFilterDropdown>
              {/*
              <SearchFilterDropdown
                DefaultValue={null}
                Id="customers-filter-orders"
                Label="Varausten määrä"
                Value={null}
              >
                <Form.Group>
                  <Form.Control type="text" />
                </Form.Group>
              </SearchFilterDropdown>
              /*}
              {/*
              <SearchFilterDropdown
                DefaultValue={null}
                Id="customers-filter-orders-dates"
                Label="Varaukset aikavälillä"
                Value={null}
              >
                <Form.Group className="mb-3">
                  <Form.Label>Alkaen</Form.Label>
                  <DatePicker locale={fi}
                    selected={startDate}
                    onChange={date => setStartDate(date)}
                    showWeekNumbers
                    className="form-control"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Päättyen</Form.Label>
                  <DatePicker locale={fi}
                    selected={startDate}
                    onChange={date => setStartDate(date)}
                    showWeekNumbers
                    className="form-control"
                  />
                </Form.Group>
              </SearchFilterDropdown>
              */}
              <div className="d-flex align-items-center p-24">
                <div className="d-flex flex-wrap align-items-center gap-8">
                  {formatSelectedCustomerGroup(
                    'Asiakasryhmä',
                    accountsSearchFilter.customerGroupId || '',
                    'customerGroupId',
                  )}
                  {formatSelectedDates(
                    'Luotu',
                    accountsSearchFilter.createdFrom,
                    accountsSearchFilter.createdTo,
                    'createdFrom',
                    'createdTo',
                  )}
                  {formatSelectedDates(
                    'Muokattu',
                    accountsSearchFilter.updatedFrom,
                    accountsSearchFilter.updatedTo,
                    'updatedFrom',
                    'updatedTo',
                  )}
                  {formatSelectedDates(
                    'Varaus luotu',
                    accountsSearchFilter.bookingCreatedFrom,
                    accountsSearchFilter.bookingCreatedTo,
                    'bookingCreatedFrom',
                    'bookingCreatedTo',
                  )}
                  {formatSelectedDates(
                    'Varaus alkaa',
                    accountsSearchFilter.bookingArrivalFrom,
                    accountsSearchFilter.bookingArrivalTo,
                    'bookingArrivalFrom',
                    'bookingArrivalTo',
                  )}
                  {formatSelectedDates(
                    'Varaus päättyy',
                    accountsSearchFilter.bookingDepartureFrom,
                    accountsSearchFilter.bookingDepartureTo,
                    'bookingDepartureFrom',
                    'bookingDepartureTo',
                  )}
                  {formatSelectedValue(
                    'Y-tunnus',
                    accountsSearchFilter.companyId || '',
                    'companyId',
                  )}
                  {formatSelectedCountry(
                    'Maa',
                    accountsSearchFilter.countryId || '',
                    'countryId',
                  )}
                  {accountsSearchFilter.fullEmailMarketingAllowed !== null &&
                    formatSelectedValue(
                      'Sähköpostimarkkinointi',
                      accountsSearchFilter.fullEmailMarketingAllowed
                        ? 'Kyllä'
                        : 'Ei',
                      'fullEmailMarketingAllowed',
                    )}
                  {accountsSearchFilter.marketingAuthorizationViaSms !== null &&
                    formatSelectedValue(
                      'SMS-markkinointi',
                      accountsSearchFilter.marketingAuthorizationViaSms
                        ? 'Kyllä'
                        : 'Ei',
                      'marketingAuthorizationViaSms',
                    )}
                  {accountsSearchFilter.invoiceByInternet !== null &&
                    formatSelectedValue(
                      'Verkkolasku',
                      accountsSearchFilter.invoiceByInternet ? 'Kyllä' : 'Ei',
                      'invoiceByInternet',
                    )}
                </div>
              </div>
            </div>
          )}
        </SearchBar>
        <Loader IsLoading={accountsLoadingStatus === LOADING_STATUS.LOADING} />
        {accountsLoadingStatus === LOADING_STATUS.IDLE &&
          true &&
          accountsTableItems.length > 0 &&
          hasSearchBeenMade && (
            <>
              <DataTable
                ColumnWidths={[120, 0, 0, 0, 0, 0, 0, 120, 120]}
                /* DeleteItem={id => handleDeleteUser(id)} */
                HideDeleteButton
                Header={[
                  'Numero',
                  'Nimi',
                  'Yrityksen nimi',
                  'Yhteyshenkilö',
                  'Osoite',
                  'Postitoimipaikka',
                  'Sähköposti',
                  'Puhelinnumero',
                  'Tunnukset',
                ]}
                ItemActions={id => {
                  const userHasAccount = true; // Replace with actual logic to check if the user has an account
                  return [
                    {
                      ButtonAction: () => {
                        if (userHasAccount) {
                          handlePopUpUserAccountLogin(id);
                        }
                      },
                      ButtonText: userHasAccount ? 'Tarkista tiedot' : '',
                    },
                  ];
                }}
                Items={accountsTableItems}
                LegendText="Asiakasta"
                TotalItems={customersPagination.totalCount}
              />
              <PaginationComponent
                OnPageClick={HandlePaginationClick}
                PaginationData={customersPagination}
              />
            </>
          )}

        {hasSearchBeenMade &&
          accountsLoadingStatus === LOADING_STATUS.IDLE &&
          customers.length < 1 && <p>Ei käyttäjiä annetuilla hakuehdoilla.</p>}
      </Container>
    </>
  );
}
export default Customers;
